/**
 * Stroke icons in the Lucide spirit. 1.5px stroke, currentColor fill,
 * 16x16 viewport. Inlined here so the redesign ships without adding
 * lucide-react as a dependency. Each icon is a small named export so
 * tree-shaking works.
 */

const W = 16;

const sw = (props: React.SVGProps<SVGSVGElement>) => ({
  width: W,
  height: W,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  ...props,
});

export const Dashboard = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </svg>
);

export const User = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <circle cx="12" cy="8" r="4" />
    <path d="M20 21a8 8 0 0 0-16 0" />
  </svg>
);

export const Resume = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
);

export const Convert = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M16 3l4 4-4 4" />
    <path d="M20 7H9a4 4 0 0 0-4 4" />
    <path d="M8 21l-4-4 4-4" />
    <path d="M4 17h11a4 4 0 0 0 4-4" />
  </svg>
);

// Climb - a summit with a flag. It was three ascending bars, which is a CHART:
// it duplicated Analytics two rows away and said nothing about climbing.
// Climb is the playful corner of the product, and a peak is the one mark that
// is both literal and light.
export const Climb = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M2.5 19.5 9 8.5l4 6.2 2.6-4 5.9 8.8Z" />
    <path d="M15.6 10.7V3.4l4.4 1.9-4.4 1.9" />
  </svg>
);

// Jobs - a briefcase with a match notch. It was a magnifier, which says
// "search" and collided with the global search that is also a magnifier: two
// different destinations wearing the same mark. Roles are BROUGHT to you here,
// ranked; nobody is searching. The notch is what separates it from a plain
// briefcase at 20px.
export const Jobs = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="2.5" y="7" width="19" height="13" rx="2" />
    <path d="M8.5 7V5.5A1.5 1.5 0 0 1 10 4h4a1.5 1.5 0 0 1 1.5 1.5V7" />
    <path d="M2.5 12.5h19" />
    <path d="M10.5 12.5v2h3v-2" />
  </svg>
);

/** A pool of people. Candidates used to wear `Skills` - a heartbeat line -
 *  which reads as a health monitor, not as human beings you are choosing
 *  between. Two figures, the back one lighter, so it says "several" at 18px
 *  without turning into a smudge. */
export const Users = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
    <circle cx="9" cy="7.5" r="3.5" />
    <path d="M22 20v-1.5a4 4 0 0 0-3-3.87" opacity="0.55" />
    <path d="M16.5 4.13a4 4 0 0 1 0 6.74" opacity="0.55" />
  </svg>
);

// Skills - a gauge. Three marks were tried and measured at 20px before this
// one: a heartbeat line (that is Activity, four rows below), a polygon inside a
// ring (reads as a map pin at nav size), and horizontal bands (reads as
// align-left). A dial is what the page actually is - every category on it
// carries a percentage - and it is the only silhouette here with an arc, so it
// cannot be confused with the line chart at Analytics.
export const Skills = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M3.2 17.5a9.5 9.5 0 1 1 17.6 0" />
    <path d="M12 17.5 16.8 9.9" />
    <circle cx="12" cy="17.5" r="1.3" />
  </svg>
);

export const Portfolio = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M9 21V9" />
  </svg>
);

export const Trophy = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

export const Analytics = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M3 3v18h18" />
    <path d="m19 9-5 5-4-4-3 3" />
  </svg>
);

// Connected sources - a hub: three things feeding one. It was a database
// cylinder (engineer-speak for storage), then briefly a plug, which says
// POWER and - worse - was also standing in for "Company profile" on the
// business side, so one mark was carrying two unrelated meanings. This is the
// literal shape of the page: GitHub, LinkedIn and the rest converging on your
// profile. Company profile now has its own mark below.
export const Source = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <circle cx="12" cy="12" r="2.4" />
    <circle cx="5" cy="5.5" r="2" />
    <circle cx="19" cy="5.5" r="2" />
    <circle cx="12" cy="20.5" r="2" />
    <path d="M10.24 10.37 6.46 6.86" />
    <path d="M13.76 10.37 17.54 6.86" />
    <path d="M12 14.4v4.1" />
  </svg>
);

/** The employer's own record: an office block with a lower annex, two windows
 *  and a door. Company profile was borrowing the connected-sources mark, which
 *  meant the business side told a recruiter their company was an integration.
 *
 *  The door is what earns its place: a stack of evenly spaced ticks reads as a
 *  chart or a list, and breaking the bottom one open at the ground line is what
 *  makes the shape a building you could walk into rather than a rectangle with
 *  lines in it. */
export const Company = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M9 21V4.6A1.6 1.6 0 0 1 10.6 3h6.3A1.6 1.6 0 0 1 18.5 4.6V21" />
    <path d="M9 10.5H4.6A1.6 1.6 0 0 0 3 12.1V21" />
    <path d="M2 21h20" />
    <path d="M12.4 7h2.8" />
    <path d="M12.4 11.5h2.8" />
    <path d="M12.2 21v-3.2h3.4V21" />
  </svg>
);

export const Activity = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export const Settings = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export const Billing = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

// A bookmark. One closed silhouette, legible at 17px, and it means the same
// thing everywhere it appears: kept, on purpose, to come back to. That IS a
// shortlist.
//
// The alternatives were worse for a rail this small. A funnel describes the
// narrowing rather than what you are left with. A ticked list is three strokes
// and two ticks - busy at this size, and indistinguishable from Insights at a
// glance. A star means "favourite", which is a rating, not a selection.
export const Bookmark = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M6 4h12a1 1 0 0 1 1 1v15l-7-4.5L5 20V5a1 1 0 0 1 1-1z" />
  </svg>
);

export const Search = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw({ width: 14, height: 14, ...p })}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

export const Bell = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

export const Moon = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
  </svg>
);

export const Sun = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

// Sun at the horizon - "dusk", the metaphor for the dim theme (light = day/Sun,
// dark = night/Moon, dim = dusk/Sunset). Rays + half-sun + horizon; no setting
// arrow (the down-chevron) so it reads as a calm sun, not a directional cue.
export const Sunset = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M12 10V2M5.6 11.6 4.2 10.2M18.4 11.6l1.4-1.4M3 18h2M19 18h2M2 22h20M16 18a4 4 0 0 0-8 0" />
  </svg>
);

export const Briefcase = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);

export const ArrowUpRight = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw({ width: 14, height: 14, ...p })}>
    <path d="M7 17 17 7" />
    <path d="M7 7h10v10" />
  </svg>
);

export const Plus = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw({ width: 14, height: 14, ...p })}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

export const Check = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw({ width: 12, height: 12, strokeWidth: 2.5, ...p })}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Pencil - inline "edit this entry" affordance.
export const Pencil = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw({ width: 13, height: 13, ...p })}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

// Sidebar collapse: two horizontal lines, top longer than bottom.
// Editorial alternative to the standard hamburger - telegraphs
// "this is the side rail, not a global menu".
export const SidebarToggle = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M4 7h16" />
    <path d="M4 12h10" />
    <path d="M4 17h13" />
  </svg>
);

// Chevrons - single (legacy) and double (edge splitter rails).
export const ChevronLeft = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);
export const ChevronRight = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);
export const ChevronsLeft = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="m11 17-5-5 5-5" />
    <path d="m18 17-5-5 5-5" />
  </svg>
);
export const ChevronsRight = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="m6 17 5-5-5-5" />
    <path d="m13 17 5-5-5-5" />
  </svg>
);

// Chat bubble - kept for places that still want a chat-icon (e.g.
// CommandPalette suggestion). The AI assistant FAB uses MarkRising
// below instead.
export const Chat = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// MarkRising - the AI assistant's signature icon. Three filled dots
// ascending diagonally; the largest is rendered in oxblood (the
// brand Mark) so the icon reads simultaneously as:
//   - level-up (rising trajectory)
//   - thinking / chat (the ellipsis)
//   - the brand Mark itself, leading the way.
// Token-pure: the small dots inherit `currentColor` from the FAB; the
// largest dot uses --accent so it stays oxblood across themes.
export const MarkRising = (p: React.SVGProps<SVGSVGElement>) => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    {...p}
  >
    <circle cx="5" cy="17.5" r="1.5" fill="currentColor" opacity="0.4" />
    <circle cx="11.5" cy="12" r="2.1" fill="currentColor" opacity="0.7" />
    <circle cx="18.5" cy="6" r="2.9" fill="var(--accent)" />
  </svg>
);

// Send / paper airplane for the chat composer.
export const Send = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="m22 2-7 20-4-9-9-4 20-7Z" />
    <path d="M22 2 11 13" />
  </svg>
);

// Attach a document to a question.
export const Paperclip = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

// A document - used on attachment chips.
export const Doc = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
  </svg>
);

// File-family icons for attachment chips. A spreadsheet, a deck and a zip are
// different enough that one generic page icon makes a row of chips unreadable
// at a glance, which is exactly when a person is scanning them.
export const Sheet = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
  </svg>
);

export const Deck = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="2" y="4" width="20" height="12" rx="2" />
    <path d="M12 16v4M8 20h8" />
  </svg>
);

export const Code = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

export const Data = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
  </svg>
);

export const Archive = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="2" y="4" width="20" height="5" rx="1.5" />
    <path d="M4 9v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9" />
    <path d="M10 13h4" />
  </svg>
);

export const Mail = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 6 10-6" />
  </svg>
);

export const ChevronDown = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Upload - drop target affordance.
/** A phone with an arrow into it. Not the generic download tray: what this
 *  hands you is an app for a device, and the glyph should say which. */
export const Download = (p: React.SVGProps<SVGSVGElement>) => (
  // sw(), like every icon in this file. Hand-rolling the attributes drew this
  // one at 18px and 1.6 stroke while its neighbours were W at 1.5, so in the
  // rail it sat a shade larger and heavier than Convert and Climb directly
  // above it - the kind of difference nobody names and everybody sees.
  <svg {...sw(p)}>
    <rect x={6} y={3} width={12} height={18} rx={2.5} />
    <path d="M12 7.5v6.5" />
    <path d="m9.25 11.5 2.75 2.75 2.75-2.75" />
    <path d="M10.5 18h3" />
  </svg>
);

export const Upload = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

// Emoji.
export const Smile = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);

// Sign out - door + arrow.
export const LogOut = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

// Card / Billing context icon for the avatar menu.
export const CreditCard = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

// X / close.
export const X = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw({ width: 14, height: 14, ...p })}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// GitBranch - the score decomposing into its factors. Used on the business
// landing, where "see how this was built" is the whole invitation.
export const GitBranch = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <line x1="6" y1="3" x2="6" y2="15" />
    <circle cx="18" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <path d="M18 9a9 9 0 0 1-9 9" />
  </svg>
);

// History - a record that is kept rather than overwritten.
export const History = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
);

// ShieldCheck - consent that was granted and is re-checked.
export const ShieldCheck = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M20 13c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V5l8-3 8 3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

// Lock - contact details that stay closed until applying opens them.
export const Lock = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Layers - many profiles reduced to one ranked list.
export const Layers = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="m12 2 9 5-9 5-9-5 9-5Z" />
    <path d="m3 12 9 5 9-5" />
    <path d="m3 17 9 5 9-5" />
  </svg>
);

// ArrowRight - the primary CTA's direction of travel.
export const ArrowRight = (p: React.SVGProps<SVGSVGElement>) => (
  <svg {...sw(p)}>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);
